import { HttpErrorResponse } from '@angular/common/http';
import { Directive, OnDestroy, OnInit, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { EmploymentType, GroupBy } from './workforce.types';
import { WorkforceApiService } from './workforce-api.service';
import { WorkforceFilters } from './filters/filters.component';

@Directive({ standalone: false })
export abstract class FilteredReport<T> implements OnInit, OnDestroy {
  protected readonly api = inject(WorkforceApiService);
  protected abstract readonly endpoint: string;
  protected abstract readonly fallbackError: string;

  groupBy: GroupBy = 'department';
  startMonth = '';
  endMonth = '';
  employmentType: EmploymentType = '';
  protected get defaultEmploymentType(): EmploymentType { return ''; }
  data: T | null = null;
  error = '';
  loading = true;
  private subscription?: Subscription;
  private requestNumber = 0;

  get invalidRange(): boolean {
    return Boolean(this.startMonth && this.endMonth && this.startMonth > this.endMonth);
  }

  ngOnInit(): void {
    this.load();
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  applyFilters(filters: WorkforceFilters): void {
    this.groupBy = filters.groupBy;
    this.startMonth = filters.startMonth;
    this.endMonth = filters.endMonth;
    this.employmentType = filters.employmentType;
    this.load();
  }

  clearFilters(): void {
    this.groupBy = 'department';
    this.startMonth = '';
    this.endMonth = '';
    this.employmentType = this.defaultEmploymentType;
    this.load();
  }

  retry(): void {
    this.load(true);
  }

  private load(force = false): void {
    this.requestNumber += 1;
    const currentRequest = this.requestNumber;
    this.subscription?.unsubscribe();
    this.data = null;
    this.error = '';
    if (this.invalidRange) {
      this.loading = false;
      return;
    }
    this.loading = true;
    this.subscription = this.api.get<T>(this.endpoint, {
      groupBy: this.groupBy,
      startMonth: this.startMonth || undefined,
      endMonth: this.endMonth || undefined,
      employmentType: this.employmentType || undefined,
    }, force).subscribe({
      next: data => {
        if (currentRequest !== this.requestNumber) return;
        this.data = data;
        this.loading = false;
      },
      error: (error: HttpErrorResponse) => {
        if (currentRequest !== this.requestNumber) return;
        const period = error.error?.period;
        this.error = error.error?.message || (Array.isArray(period) ? period[0] : period) || this.fallbackError;
        this.loading = false;
      },
    });
  }
}
